const LATEX_MACROS: Record<string, string> = {
  to: '→',
  rightarrow: '→',
  Rightarrow: '⇒',
  leftarrow: '←',
  Leftarrow: '⇐',
  leftrightarrow: '↔',
  Leftrightarrow: '⇔',
  uparrow: '↑',
  downarrow: '↓',
  Uparrow: '⇑',
  Downarrow: '⇓',
  mapsto: '↦',

  sum: 'Σ',
  prod: 'Π',
  int: '∫',
  infty: '∞',
  partial: '∂',
  nabla: '∇',

  leq: '≤',
  le: '≤',
  geq: '≥',
  ge: '≥',
  neq: '≠',
  ne: '≠',
  approx: '≈',
  equiv: '≡',
  sim: '∼',
  propto: '∝',

  times: '×',
  cdot: '·',
  div: '÷',
  pm: '±',
  mp: '∓',
  circ: '∘',
  bullet: '•',
  ast: '∗',

  alpha: 'α',
  beta: 'β',
  gamma: 'γ',
  delta: 'δ',
  epsilon: 'ε',
  zeta: 'ζ',
  eta: 'η',
  theta: 'θ',
  iota: 'ι',
  kappa: 'κ',
  lambda: 'λ',
  mu: 'μ',
  nu: 'ν',
  xi: 'ξ',
  pi: 'π',
  rho: 'ρ',
  sigma: 'σ',
  tau: 'τ',
  upsilon: 'υ',
  phi: 'φ',
  chi: 'χ',
  psi: 'ψ',
  omega: 'ω',

  Gamma: 'Γ',
  Delta: 'Δ',
  Theta: 'Θ',
  Lambda: 'Λ',
  Xi: 'Ξ',
  Pi: 'Π',
  Sigma: 'Σ',
  Phi: 'Φ',
  Psi: 'Ψ',
  Omega: 'Ω',

  degree: '°',
};

// 길이 내림차순 — \le 보다 \leq 가 먼저 매칭되도록.
const MACRO_NAMES_BY_LENGTH = Object.keys(LATEX_MACROS).sort((a, b) => b.length - a.length);
const ALT = MACRO_NAMES_BY_LENGTH.join('|');

// 일반 매크로. 뒤에 알파벳이 이어지면 다른 매크로일 수 있으니 negative lookahead.
const BARE_MACRO = new RegExp(`\\\\(${ALT})(?![a-zA-Z])`, 'g');

// 한 줄 안의 인라인 수식 스팬 `$...$`. `[^$\n]` 문자클래스가 `$` 를 못 넘으므로 같은 줄의
// 여러 스팬 (표 셀 등) 이 각각 독립 매칭된다.
const INLINE_MATH = /\$([^$\n]+)\$/g;
// `\text{ kW}` 처럼 단위/텍스트 래퍼 — 안쪽 내용만 남긴다 (선행 공백 보존).
const TEXT_WRAP = /\\text\{([^{}]*)\}/g;
// `^\circ` / `^{\circ}` — 온도 도 기호.
const SUP_CIRC = /\^\{?\\circ\}?/g;
// LaTeX spacing 명령.
const SPACING = /\\[,;:!]/g;
// 변환 후에도 남아있으면 우리가 안전히 렌더 못 하는 진짜 수식 (\frac, x^2, a_i 등).
const UNRENDERABLE = /[\\^_{}]/;

/**
 * `$...$` 인라인 수식 스팬을 원자적으로 정규화한다. inner 에 백슬래시가 없으면 통화 표기
 * (`$50`) 로 보고 그대로 둔다 — 이 가드가 안전성의 핵심. 변환 후에도 렌더 불가한 수식 토큰이
 * 남으면 반쪽 mangle 대신 원본 `$...$` 를 보존한다.
 */
function normalizeInlineMath(whole: string, inner: string): string {
  if (!inner.includes('\\')) return whole;
  const s = inner
    .replace(TEXT_WRAP, '$1')
    .replace(SUP_CIRC, '°')
    .replace(/\\%/g, '%')
    .replace(SPACING, ' ')
    .replace(
      new RegExp(`\\\\(${ALT})(?![a-zA-Z])`, 'g'),
      (_m, name: string) => LATEX_MACROS[name] ?? _m,
    )
    .replace(/\s+/g, ' ')
    .trim();
  if (UNRENDERABLE.test(s)) return whole;
  return s;
}

// `$\color{red}{\text{▲ 15%}}$` / KaTeX 식 `$\textcolor{red}{...}$` 처럼 모델이 색 강조를
// LaTeX 로 출력한 패턴. 둘러싼 `$` 와 안쪽 `\text{}` 는 옵셔널. 색 이름은 `\w+` 로 제한해
// 안전한 `<span color="...">` 로 치환 — `color` 속성은 rehype-sanitize defaultSchema 의
// 전역 화이트리스트라 그대로 통과하고, Markdown.tsx 의 span 컴포넌트가 테마 토큰색으로 매핑한다.
const COLOR_LATEX = /\$?\\(?:text)?color\{(\w+)\}\{(?:\\text\{)?([^{}]*?)\}?\}\$?/g;

// 코드 펜스 / 인라인 코드 보호. 이 영역의 텍스트는 매크로 치환에서 제외한다.
const PROTECTED = /```[\s\S]*?```|`[^`\n]*`/g;
// 마크다운/사용자 본문에 등장 안 하는 control 문자로 sentinel 구성.
const SENTINEL_OPEN = '\u200BPROT_';
const SENTINEL_CLOSE = '_PROT\u200B';
const SENTINEL_PATTERN = /\u200BPROT_(\d+)_PROT\u200B/g;

/**
 * 모델이 한국어 본문에 LaTeX 매크로 (`\rightarrow`, `\sum` 등) 와 인라인 수식 구분자 (`$...$`)
 * 를 박아서 raw 텍스트로 깨져 보이는 케이스를 안전망으로 후처리한다. 자주 쓰는 매크로 ~70개만
 * 유니코드 단일 문자로 치환. 분수/적분 같은 진짜 수식은 변환하지 않는다 (KaTeX 미사용).
 * `\color{}{\text{}}` 색 강조만 예외로 `<span color>` 로 해석한다.
 *
 * 코드 펜스 / 인라인 백틱은 건드리지 않는다 — SQL placeholder, ```chart JSON 등 보호.
 */
export function applyLatexFallbacks(text: string): string {
  if (!text) return text;
  const stash: string[] = [];
  const masked = text.replace(PROTECTED, (m) => {
    stash.push(m);
    return `${SENTINEL_OPEN}${stash.length - 1}${SENTINEL_CLOSE}`;
  });

  const transformed = masked
    .replace(
      COLOR_LATEX,
      (_, name: string, inner: string) =>
        `<span color="${name}">${inner.replace(/\\%/g, '%').trim()}</span>`,
    )
    .replace(INLINE_MATH, normalizeInlineMath)
    .replace(BARE_MACRO, (_, name: string) => LATEX_MACROS[name] ?? `\\${name}`)
    .replace(/\\\$/g, '$');

  return transformed.replace(SENTINEL_PATTERN, (_, idx: string) => stash[Number(idx)] ?? '');
}
